import React from 'react';
import { ArrowRight, MapPin, Briefcase, TrendingUp, ChevronLeft, ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const JobsPage = ({
  jobs,
  setCurrentPage,
  goToLeaderboard,
  mobileMenuOpen,
  setMobileMenuOpen,
  jobFilters,
  setJobFilters
}) => {
  const [currentJobPage, setCurrentJobPage] = React.useState(1);
  const jobsPerPage = 8;

  // Extract unique values for filters
  const locations = [...new Set(jobs.map(job => job.location))];
  const types = [...new Set(jobs.map(job => job.type))];
  const levels = [...new Set(jobs.map(job => job.level))];

  // Filter jobs based on selected filters
  const filteredJobs = jobs.filter(job => {
    return (
      (jobFilters.location === '' || job.location === jobFilters.location) &&
      (jobFilters.type === '' || job.type === jobFilters.type) &&
      (jobFilters.level === '' || job.level === jobFilters.level)
    );
  });

  const handleFilterChange = (filterType, value) => {
    setJobFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    setCurrentJobPage(1); // Reset to first page when filters change
  };

  const clearFilters = () => {
    setJobFilters({
      location: '',
      type: '',
      level: ''
    });
    setCurrentJobPage(1); // Reset to first page when clearing filters
  };

  // Calculate pagination
  const totalPages = Math.ceil(filteredJobs.length / jobsPerPage);
  const startIndex = (currentJobPage - 1) * jobsPerPage;
  const endIndex = startIndex + jobsPerPage;
  const currentJobs = filteredJobs.slice(startIndex, endIndex);

  const handlePageChange = (pageNumber) => {
    setCurrentJobPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openJob = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 via-purple-600 to-purple-800 overflow-x-hidden flex flex-col">
      <Header 
        setCurrentPage={setCurrentPage} 
        goToLeaderboard={goToLeaderboard}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      <div className="flex-1 pt-20 p-4">
        <div className="max-w-6xl mx-auto">
          {/* Page Title */}
          <div className="text-center mb-8 text-white">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-2">
              Find your next opportunity
            </h1>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location
                </label>
                <select
                  value={jobFilters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">All Locations</option>
                  {locations.map(loc => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))}
                </select>
              </div>

              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type
                </label>
                <select
                  value={jobFilters.type}
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">All Types</option>
                  {types.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Level
                </label>
                <select
                  value={jobFilters.level}
                  onChange={(e) => handleFilterChange('level', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">All Levels</option>
                  {levels.map(level => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
              </div>

              <button
                onClick={clearFilters}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Clear Filters
              </button>
            </div>

            <div className="mt-4 text-sm text-gray-600">
              Showing {filteredJobs.length === 0 ? 0 : startIndex + 1}-{Math.min(endIndex, filteredJobs.length)} of {filteredJobs.length} jobs
            </div>
          </div>

          {/* Jobs Grid */}
          <div className="grid gap-4 md:gap-6 mb-8">
            {filteredJobs.length === 0 ? (
              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <p className="text-gray-500 text-lg">
                  No jobs found matching your filters.
                </p>
              </div>
            ) : (
              currentJobs.map(job => (
                <div
                  key={job.id}
                  className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow cursor-pointer group"
                  onClick={() => openJob(job.url)}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3 group-hover:text-purple-600 transition-colors">
                        {job.title}
                      </h3>
                      
                      <div className="flex flex-wrap gap-3 mb-4">
                        <div className="flex items-center text-gray-600">
                          <MapPin className="w-4 h-4 mr-1" />
                          <span className="text-sm">{job.location}</span>
                        </div>
                        
                        <div className="flex items-center text-gray-600">
                          <Briefcase className="w-4 h-4 mr-1" />
                          <span className="text-sm">{job.type}</span>
                        </div>
                        
                        <div className="flex items-center text-gray-600">
                          <TrendingUp className="w-4 h-4 mr-1" />
                          <span className="text-sm">{job.level}</span>
                        </div>
                      </div>

                      <div className="flex items-center text-purple-600 font-medium group-hover:text-purple-700">
                        <span className="mr-2">View Job Details</span>
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                    
                    <div className="flex-shrink-0 flex items-center">
                      {job.logo ? (
                        <img 
                          src={job.logo} 
                          alt={job.company}
                          className="w-24 h-24 sm:w-28 sm:h-28 object-contain rounded-lg"
                        />
                      ) : (
                        <div className="flex items-center justify-center min-w-[80px]">
                          <span className="text-gray-900 font-bold text-lg sm:text-xl text-center break-words">
                            {job.company || 'Company'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 pb-8">
              <button
                onClick={() => handlePageChange(currentJobPage - 1)}
                disabled={currentJobPage === 1}
                className="p-2 rounded-lg bg-white text-purple-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-purple-50 transition-colors"
              >
                <ChevronLeft size={20} />
              </button>
              
              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index + 1}
                  onClick={() => handlePageChange(index + 1)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    currentJobPage === index + 1
                      ? 'bg-white text-purple-600'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  {index + 1}
                </button>
              ))}
              
              <button
                onClick={() => handlePageChange(currentJobPage + 1)}
                disabled={currentJobPage === totalPages}
                className="p-2 rounded-lg bg-white text-purple-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-purple-50 transition-colors"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="h-32"></div>
      <Footer setCurrentPage={setCurrentPage} goToLeaderboard={goToLeaderboard} />
    </div>
  );
};

export default JobsPage;